<div class="w-[920px] h-[580px] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col">

    <!-- ── Header ─────────────────────────────────────────────────────── -->
    <div class="flex items-center justify-between px-8 pt-7 pb-5 border-b border-slate-100">
        <div class="flex items-center gap-4">
            <!-- Avatar -->
            <div class="w-14 h-14 rounded-full bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center font-bold text-white text-xl shadow select-none">
                {{ userInitials() }}
            </div>
            <div class="flex flex-col">
                <span class="text-[18px] font-bold text-slate-800 leading-tight">{{ authService.user()?.name }}</span>
                <span class="text-sm text-slate-400 font-medium mt-0.5">{{ roleLabel() }}</span>
            </div>
        </div>
        <!-- Close button emits to parent via ContentChild — sidebar handles it -->
        <button type="button" (click)="closeRequested()" id="user-settings-close-btn"
            class="w-9 h-9 flex items-center justify-center rounded-full border border-slate-200 text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-colors">
            <span class="material-icons text-[18px]">close</span>
        </button>
    </div>

    <!-- ── Body ───────────────────────────────────────────────────────── -->
    <div class="flex flex-1 min-h-0 overflow-hidden">

        <!-- Left Sidebar: tabs -->
        <div class="w-[220px] shrink-0 p-5 flex flex-col gap-1 border-r border-slate-100">
            <!-- Change Secure Pin tab -->
            <button type="button" (click)="selectTab('secure-pin')"
                class="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-150 text-left w-full"
                [ngClass]="activeTab() === 'secure-pin'
                    ? 'bg-slate-100 text-slate-900 font-semibold'
                    : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600'">
                <span class="material-icons text-[18px]">lock_reset</span>
                Change Secure Pin
            </button>

            <!-- Change Password tab -->
            <button type="button" (click)="selectTab('password')"
                class="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-150 text-left w-full"
                [ngClass]="activeTab() === 'password'
                    ? 'bg-slate-100 text-slate-900 font-semibold'
                    : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600'">
                <span class="material-icons text-[18px]">key</span>
                Change Password
            </button>
        </div>

        <!-- Right content panel -->
        <div class="flex-1 min-w-0 overflow-y-auto p-7">

            <!-- ── Change Secure PIN Tab ───────────────────────────────── -->
            @if (activeTab() === 'secure-pin') {
            <form [formGroup]="pinForm" (ngSubmit)="savePin()" class="flex flex-col gap-5">

                <!-- Current Password -->
                <div class="flex flex-col gap-1.5">
                    <label class="text-xs font-bold text-slate-500 uppercase tracking-wider">
                        CURRENT PASSWORD <span class="text-red-500">*</span>
                    </label>
                    <div class="relative">
                        <input formControlName="currentPassword" [type]="showCurrentPassword() ? 'text' : 'password'"
                            placeholder="Enter current password"
                            class="w-full px-4 py-3 pr-12 border rounded-xl text-sm text-slate-800 placeholder:text-slate-300 outline-none transition-colors"
                            [ngClass]="pinForm.get('currentPassword')?.touched && pinForm.get('currentPassword')?.invalid
                                ? 'border-red-400 focus:border-red-500'
                                : 'border-slate-200 focus:border-blue-400'" />
                        <button type="button" (click)="showCurrentPassword.set(!showCurrentPassword())"
                            class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                            <img [src]="showCurrentPassword() ? 'assets/icons/visibility.svg' : 'assets/icons/visibility_off.svg'"
                                class="w-5 h-5" alt="toggle" />
                        </button>
                    </div>
                    @if (pinError('currentPassword')) {
                    <span class="text-xs text-red-500 animate-in fade-in">{{ pinError('currentPassword') }}</span>
                    }
                </div>

                <!-- New Secure PIN -->
                <div class="flex flex-col gap-1.5">
                    <label class="text-xs font-bold text-slate-500 uppercase tracking-wider">
                        NEW SECURE PIN <span class="text-red-500">*</span>
                    </label>
                    <div class="relative">
                        <input formControlName="newPin" [type]="showNewPin() ? 'text' : 'password'"
                            placeholder="Enter 6-digit PIN" maxlength="6"
                            class="w-full px-4 py-3 pr-12 border rounded-xl text-sm text-slate-800 placeholder:text-slate-300 outline-none transition-colors"
                            [ngClass]="pinForm.get('newPin')?.touched && pinForm.get('newPin')?.invalid
                                ? 'border-red-400 focus:border-red-500'
                                : 'border-slate-200 focus:border-blue-400'" />
                        <button type="button" (click)="showNewPin.set(!showNewPin())"
                            class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                            <img [src]="showNewPin() ? 'assets/icons/visibility.svg' : 'assets/icons/visibility_off.svg'"
                                class="w-5 h-5" alt="toggle" />
                        </button>
                    </div>
                    @if (pinError('newPin')) {
                    <span class="text-xs text-red-500 animate-in fade-in">{{ pinError('newPin') }}</span>
                    }
                </div>

                <!-- Confirm New Secure PIN -->
                <div class="flex flex-col gap-1.5">
                    <label class="text-xs font-bold text-slate-500 uppercase tracking-wider">
                        CONFIRM NEW SECURE PIN <span class="text-red-500">*</span>
                    </label>
                    <div class="relative">
                        <input formControlName="confirmPin" [type]="showConfirmPin() ? 'text' : 'password'"
                            placeholder="Re-enter 6-digit PIN" maxlength="6"
                            class="w-full px-4 py-3 pr-12 border rounded-xl text-sm text-slate-800 placeholder:text-slate-300 outline-none transition-colors"
                            [ngClass]="pinForm.get('confirmPin')?.touched && pinForm.get('confirmPin')?.invalid
                                ? 'border-red-400 focus:border-red-500'
                                : 'border-slate-200 focus:border-blue-400'" />
                        <button type="button" (click)="showConfirmPin.set(!showConfirmPin())"
                            class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                            <img [src]="showConfirmPin() ? 'assets/icons/visibility.svg' : 'assets/icons/visibility_off.svg'"
                                class="w-5 h-5" alt="toggle" />
                        </button>
                    </div>
                    @if (pinError('confirmPin')) {
                    <span class="text-xs text-red-500 animate-in fade-in">{{ pinError('confirmPin') }}</span>
                    }
                </div>

                <!-- Actions -->
                <div class="flex items-center justify-end gap-3 pt-2">
                    <button type="button" (click)="clearPin()"
                        class="px-6 py-2.5 text-sm font-semibold text-slate-500 hover:text-slate-700 transition-colors">
                        Clear
                    </button>
                    <button type="submit"
                        class="px-8 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                        [disabled]="isSavingPin() || pinForm.invalid || pinForm.pristine">
                        @if (isSavingPin()) {
                        <span class="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></span>
                        }
                        Update
                    </button>
                </div>
            </form>
            }

            <!-- ── Change Password Tab ─────────────────────────────────── -->
            @if (activeTab() === 'password') {
            <form [formGroup]="passwordForm" (ngSubmit)="savePassword()" class="flex flex-col gap-5">

                <!-- Current Password -->
                <div class="flex flex-col gap-1.5">
                    <label class="text-xs font-bold text-slate-500 uppercase tracking-wider">
                        CURRENT PASSWORD <span class="text-red-500">*</span>
                    </label>
                    <div class="relative">
                        <input formControlName="currentPassword" [type]="showCurrentPwd() ? 'text' : 'password'"
                            placeholder="Enter current password"
                            class="w-full px-4 py-3 pr-12 border rounded-xl text-sm text-slate-800 placeholder:text-slate-300 outline-none transition-colors"
                            [ngClass]="passwordForm.get('currentPassword')?.touched && passwordForm.get('currentPassword')?.invalid
                                ? 'border-red-400 focus:border-red-500'
                                : 'border-slate-200 focus:border-blue-400'" />
                        <button type="button" (click)="showCurrentPwd.set(!showCurrentPwd())"
                            class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                            <img [src]="showCurrentPwd() ? 'assets/icons/visibility.svg' : 'assets/icons/visibility_off.svg'"
                                class="w-5 h-5" alt="toggle" />
                        </button>
                    </div>
                    @if (passwordError('currentPassword')) {
                    <span class="text-xs text-red-500 animate-in fade-in">{{ passwordError('currentPassword') }}</span>
                    }
                </div>

                <!-- New Password -->
                <div class="flex flex-col gap-1.5">
                    <label class="text-xs font-bold text-slate-500 uppercase tracking-wider">
                        NEW PASSWORD <span class="text-red-500">*</span>
                    </label>
                    <div class="relative">
                        <input formControlName="newPassword" [type]="showNewPwd() ? 'text' : 'password'"
                            placeholder="Enter new password (min. 8 chars)"
                            class="w-full px-4 py-3 pr-12 border rounded-xl text-sm text-slate-800 placeholder:text-slate-300 outline-none transition-colors"
                            [ngClass]="passwordForm.get('newPassword')?.touched && passwordForm.get('newPassword')?.invalid
                                ? 'border-red-400 focus:border-red-500'
                                : 'border-slate-200 focus:border-blue-400'" />
                        <button type="button" (click)="showNewPwd.set(!showNewPwd())"
                            class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                            <img [src]="showNewPwd() ? 'assets/icons/visibility.svg' : 'assets/icons/visibility_off.svg'"
                                class="w-5 h-5" alt="toggle" />
                        </button>
                    </div>
                    @if (passwordError('newPassword')) {
                    <span class="text-xs text-red-500 animate-in fade-in">{{ passwordError('newPassword') }}</span>
                    }
                </div>

                <!-- Confirm New Password -->
                <div class="flex flex-col gap-1.5">
                    <label class="text-xs font-bold text-slate-500 uppercase tracking-wider">
                        CONFIRM NEW PASSWORD <span class="text-red-500">*</span>
                    </label>
                    <div class="relative">
                        <input formControlName="confirmPassword" [type]="showConfirmPwd() ? 'text' : 'password'"
                            placeholder="Re-enter new password"
                            class="w-full px-4 py-3 pr-12 border rounded-xl text-sm text-slate-800 placeholder:text-slate-300 outline-none transition-colors"
                            [ngClass]="passwordForm.get('confirmPassword')?.touched && passwordForm.get('confirmPassword')?.invalid
                                ? 'border-red-400 focus:border-red-500'
                                : 'border-slate-200 focus:border-blue-400'" />
                        <button type="button" (click)="showConfirmPwd.set(!showConfirmPwd())"
                            class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                            <img [src]="showConfirmPwd() ? 'assets/icons/visibility.svg' : 'assets/icons/visibility_off.svg'"
                                class="w-5 h-5" alt="toggle" />
                        </button>
                    </div>
                    @if (passwordError('confirmPassword')) {
                    <span class="text-xs text-red-500 animate-in fade-in">{{ passwordError('confirmPassword') }}</span>
                    }
                </div>

                <!-- Actions -->
                <div class="flex items-center justify-end gap-3 pt-2">
                    <button type="button" (click)="clearPassword()"
                        class="px-6 py-2.5 text-sm font-semibold text-slate-500 hover:text-slate-700 transition-colors">
                        Clear
                    </button>
                    <button type="submit"
                        class="px-8 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                        [disabled]="isSavingPassword() || passwordForm.invalid || passwordForm.pristine">
                        @if (isSavingPassword()) {
                        <span class="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></span>
                        }
                        Update Password
                    </button>
                </div>
            </form>
            }

        </div>
    </div>
</div>
